import { useTheme } from '@emotion/react';
import {
  Badge,
  Box,
  Button,
  ClickAwayListener,
  Dialog,
  Popper,
  Typography,
} from '@mui/material';
import { tokens } from '../../../global/theme/tokens';
import { useEffect, useState } from 'react';
import NewAddress from '../../new/NewAddress';
import { useTranslation } from 'react-i18next';
import LabeledBox from '../../special/LabeledBox';
import PersonPinIcon from '@mui/icons-material/PersonPin';
import { useConfig } from '../../../api/api';
import { updateOrCreate } from '../../special/updateOrCreate';

const TypeAddress = ({
  field,
  updateValidationOnChange,
  dataCollection,
  errors,
  size,
  onPreventEnter = () => {},
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const [valid, setValid] = useState(false);
  const [open, setOpen] = useState(false);
  const [addressData, setAddressData] = useState(
    { ...dataCollection[field.key] } || {}
  );
  const config = useConfig();

  // reset address
  const addressFallBack = { ...dataCollection[field.key] } || {};
  function handleCancel() {
    updateValidationOnChange(field.key, addressFallBack);
    setOpen(false);
    onPreventEnter(false);
  }

  function handleSave() {
    updateValidationOnChange(field.key, addressData);
    setOpen(false);
    onPreventEnter(false);
  }

  function handleUpdate(data) {
    setAddressData(data);
  }

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Enter' && valid) {
        event.preventDefault(); // Prevent default form submission behavior
        handleSave();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [dataCollection, valid]);

  // suggestions for address auto fill
  const [suggestions, setSuggestions] = useState([]);
  const [anchor, setAnchor] = useState(null);
  const handleClick = (event) => {
    setAnchor(anchor ? null : event.currentTarget);
  };

  useEffect(() => {
    var newSuggestions = [];
    // add own address
    if (field.allowOwnAddress && config)
      newSuggestions.push({
        label: t('My Company'),
        address: config.companyAddress,
      });

    if (!Boolean(field.suggestionsArr)) return setSuggestions(newSuggestions);

    field.suggestionsArr.forEach((sugg) => {
      const rawData = dataCollection[sugg.key];
      if (!rawData) return;
      if (sugg.isArray) {
        rawData.forEach((rawDataElement) => {
          newSuggestions = updateOrCreate(
            newSuggestions,
            convertSuggestionsTypeMap[sugg.type](rawDataElement, sugg)
          );
        });
      } else {
        newSuggestions = updateOrCreate(
          newSuggestions,
          convertSuggestionsTypeMap[sugg.type](rawData, sugg)
        );
      }
    });
    const filteredSuggestions = newSuggestions.filter((obj) => obj.address);

    setSuggestions(filteredSuggestions);
  }, [dataCollection, config]);

  const convertSuggestionsTypeMap = {
    participant: convertPerticipantIntoSuggestion,
    client: convertClientIntoSuggestion,
  };

  function convertPerticipantIntoSuggestion(element, sugg) {
    const { type, participant } = element;
    const label = convertElementIntoLabel(participant, type);
    var address = participant?.address;
    if (address && !address.countryCode) {
      address = { ...address, country: 'Germany', countryCode: 'DE' };
    }

    return {
      label,
      address,
      id: `${type}-${participant?.id}`,
    };
  }

  function convertClientIntoSuggestion(element, sugg) {
    const type = dataCollection[sugg.key + 'Type'];
    const label = convertElementIntoLabel(element, type);

    var address = element?.address;
    if (address && !address.countryCode) {
      address = { ...address, country: 'Germany', countryCode: 'DE' };
    }

    return { label, address, id: `${type}-${element?.id}` };
  }

  function convertElementIntoLabel(element, type) {
    if (!element) return {};
    const typeLabelMap = {
      company: (e) => e.nameShort || e.name,
      companyLead: (e) => e.nameShort || e.name,
      contact: (e) => `${e.salutation} ${e.firstName} ${e.lastName}`,
      personLead: (e) => `${e.salutation} ${e.firstName} ${e.lastName}`,
      user: (e) => `${e.salutation} ${e.firstName} ${e.lastName}`,
    };
    if (!typeLabelMap[type])
      console.warn('No Address Element Label Type exists with the type', type);
    return typeLabelMap[type](element);
  }

  function handleSuggClick(sugg) {
    updateValidationOnChange([field.key], sugg.address);
    setAnchor(null);
  }

  return (
    <>
      <Box className="w-full flex gap-2">
        <LabeledBox
          sx={{ width: '100%' }}
          onClick={() => {
            setOpen(true);
            onPreventEnter(true);
          }}
          size={size}
          label={dataCollection[field.key] ? field.label : ''}
        >
          {dataCollection[field.key] && dataCollection[field.key].street
            ? dataCollection[field.key].street +
              ' ' +
              dataCollection[field.key].houseNumber +
              ', ' +
              dataCollection[field.key].zipCode +
              ' ' +
              dataCollection[field.key].city +
              ', ' +
              dataCollection[field.key].country
            : field.label}
        </LabeledBox>
        {suggestions.length > 0 && (
          <>
            <Badge badgeContent={suggestions.length} color="info" size="small">
              <Button
                sx={{
                  bgcolor: colors.bgInput,
                  borderRadius: '10px',
                  minWidth: size === 'small' ? '40px' : '53px',
                }}
                onClick={handleClick}
              >
                <PersonPinIcon />
              </Button>
            </Badge>
            <Popper
              anchorEl={anchor}
              open={Boolean(anchor)}
              sx={{ zIndex: 9999 }}
              className="flex justify-center flex-col items-end"
              placement="bottom-end"
            >
              <Box
                sx={{
                  width: 0,
                  height: 0,
                  borderLeft: '5px solid transparent',
                  borderRight: '5px solid transparent',
                  borderBottom: `5px solid ${colors.selected}`,
                  marginRight: size === 'small' ? '15px' : '25px',
                }}
              />
              <ClickAwayListener onClickAway={() => setAnchor(null)}>
                <Box
                  className="rounded-lg px-2 py-1 shadow-md flex flex-col gap-1 backdrop-blur-md"
                  sx={{
                    background: `linear-gradient(135deg,  ${colors.glassFrom}, ${colors.glassTo})`,
                    border: `1px solid ${colors.selected}`,
                  }}
                >
                  <Typography variant="h6">
                    {t('Quick address selection')}
                  </Typography>
                  {suggestions.map((sugg) => (
                    <Button
                      onClick={() => handleSuggClick(sugg)}
                      sx={{
                        justifyContent: 'start',
                        alignItems: 'start',
                        border: `1px solid transparent`,
                        ':hover': {
                          bgcolor: colors.info + '30',
                          border: `1px solid ${colors.info}`,
                        },
                      }}
                      className="flex-col"
                    >
                      <Typography fontWeight={500}>{sugg.label}</Typography>
                      <Typography
                        fontSize={12}
                        className="opacity-70"
                      >{`${sugg.address.street} ${sugg.address.houseNumber} - ${sugg.address.zipCode} ${sugg.address.city}`}</Typography>
                    </Button>
                  ))}
                </Box>
              </ClickAwayListener>
            </Popper>
          </>
        )}
      </Box>

      <Dialog open={open}>
        <Box className=" flex gap-3 flex-col p-2">
          <Typography variant="h3" textAlign="center">
            {t('Address')}
          </Typography>
          <NewAddress
            updateDataCollection={handleUpdate}
            dataCollection={dataCollection}
            updateValidation={setValid}
            fieldKey={field.key}
          />
          <Box className="w-full flex justify-between">
            <Button onClick={handleCancel}>{t('Cancel')}</Button>
            <Button
              onClick={handleSave}
              variant="contained"
              color="success"
              disabled={!valid}
            >
              {t('Save')}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default TypeAddress;
